/* eslint-disable max-lines */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';

import Button from '@App/components/Button/Button';
import Keyboard from '@App/components/Keyboard/Keyboard';
import LanguageSwitch from '@App/components/LanguageSwitch/LanguageSwitch';
import { useTranslation } from '@utils/i18n/i18n';

import postalLocalities from './data/belgian-postal-localities.json';
import { getCountryOptions } from './data/country-options';
import { resolveResidenceCoordinates } from '../Question2/municipality-data';

import './introduction.css';

const transportOptions = [
  { value: 'Trein', translationKey: 'introduction-transport-train' },
  { value: 'Fiets', translationKey: 'introduction-transport-bike' },
  { value: 'Auto', translationKey: 'introduction-transport-car' },
  { value: 'Bus of tram', translationKey: 'introduction-transport-bus-or-tram' },
  { value: 'Te voet', translationKey: 'introduction-transport-walking' },
];
const educationOptions = [
  { value: 'Basisonderwijs', translationKey: 'introduction-education-primary' },
  { value: 'Middelbaar onderwijs', translationKey: 'introduction-education-secondary' },
  { value: 'Hoger onderwijs', translationKey: 'introduction-education-higher' },
  { value: 'Anders', translationKey: 'introduction-education-other' },
];
const userTypes = [
  { value: 'Denker', translationKey: 'introduction-user-type-thinker' },
  { value: 'Doener', translationKey: 'introduction-user-type-doer' },
  { value: 'Dromer', translationKey: 'introduction-user-type-dreamer' },
  { value: 'Beslisser', translationKey: 'introduction-user-type-decider' },
];
const localityNameCounts = postalLocalities.reduce((counts, locality) => {
  counts.set(locality.name, (counts.get(locality.name) ?? 0) + 1);
  return counts;
}, new Map());

function normalizeSearchValue(value) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('nl-BE');
}

function getLocalityLabel(locality) {
  if (localityNameCounts.get(locality.name) > 1) {
    return `${locality.name} (${locality.province})`;
  }

  return locality.name;
}

function getPostcodeLabel(locality) {
  if (locality.postcodes.length < 2) return locality.postcodes.join(', ');

  const sortedPostcodes = locality.postcodes.map(Number).sort((first, second) => first - second);

  return `${sortedPostcodes[0]}-${sortedPostcodes[sortedPostcodes.length - 1]}`;
}

function getLocalityKey(locality) {
  return `${locality.name}|${locality.region}|${locality.province}|${locality.postcodes.join(',')}`;
}

function getResidenceOptionKey(option) {
  return option.value || getLocalityKey(option);
}

function Introduction({ enableKeyboard = true }) {
  const { state: locationState } = useLocation();
  const { onStartParticipant } = useOutletContext();
  const navigate = useNavigate();
  const { lang, t } = useTranslation();
  const [showProfileForm, setShowProfileForm] = useState(
    locationState?.isProfileFormOpen === true,
  );
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [isResidenceSearchOpen, setIsResidenceSearchOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [activeInput, setActiveInput] = useState('age');
  const [profile, setProfile] = useState({
    age: '', residence: '', transport: '', education: '', userType: '',
  });
  const residenceQuery = normalizeSearchValue(profile.residence.trim());
  const countryOptions = getCountryOptions(lang);
  const residenceOptions = residenceQuery
    ? [
      ...postalLocalities
        .filter(locality => normalizeSearchValue([
          locality.name, locality.region, locality.province, ...locality.postcodes,
        ].join(' ')).includes(residenceQuery))
        .map(locality => ({ ...locality, label: getLocalityLabel(locality) })),
      ...countryOptions.filter(country => Object.values(country.names)
        .some(name => normalizeSearchValue(name).includes(residenceQuery))),
    ].slice(0, 8)
    : [];

  useEffect(() => {
    setShowProfileForm(locationState?.isProfileFormOpen === true);
  }, [locationState?.isProfileFormOpen]);

  function updateProfileField(name, value) {
    let normalizedValue = value;
    document.querySelectorAll(`[name="${name}"]`).forEach((field) => {
      field.setCustomValidity('');
    });
    if (name === 'age') {
      normalizedValue = value.replace(/\D/g, '').slice(0, 3);
      if (normalizedValue && Number(normalizedValue) < 1) return;
    }
    setProfile(currentProfile => ({ ...currentProfile, [name]: normalizedValue }));
  }

  function handleProfileChange(event) {
    const { name, value } = event.target;
    updateProfileField(name, value);
  }

  function handleAgeKeyDown(event) {
    if (['-', '+', 'e', 'E', '.', ','].includes(event.key)) event.preventDefault();
  }

  function handleProfileInvalid(event) {
    event.target.setCustomValidity(t('introduction-required-field'));
  }

  function handleProfileInput(event) {
    event.target.setCustomValidity('');
  }

  async function handleProfileSubmit(event) {
    event.preventDefault();
    const residenceCoordinates = await resolveResidenceCoordinates(profile.residence);

    onStartParticipant({
      ...profile,
      residenceCoordinates,
    });
    navigate('question1');
  }

  return (
    <section className={`introduction${showProfileForm ? ' introduction--form-open' : ''}${isKeyboardOpen ? ' introduction--keyboard-open' : ''}`} style={{ '--keyboard-height': `${keyboardHeight || 196}px` }}>
      <div className="introduction__content">
        {!showProfileForm ? (
          <div className="introduction__welcome">
            <div className="introduction__language-switch"><LanguageSwitch color="#5d7a69" /></div>
            <p className="introduction__eyebrow">{t('introduction-welcome-eyebrow')}</p>
            <h1 className="introduction__title">{t('introduction-welcome-title')}</h1>
            <p className="introduction__text">{t('introduction-welcome-text')}</p>
            <Button onClick={() => setShowProfileForm(true)}>
              {t('introduction-welcome-button')}
            </Button>
          </div>
        ) : (
          <form className={`profile-form${isKeyboardOpen ? ' profile-form--keyboard-open' : ''}`} onSubmit={handleProfileSubmit} onInvalid={handleProfileInvalid} onInput={handleProfileInput}>
            <div className="profile-form__header">
              <p className="introduction__eyebrow">{t('introduction-profile-eyebrow')}</p>
              <h1 className="introduction__title">{t('introduction-profile-title')}</h1>
            </div>
            <fieldset className="profile-form__group profile-form__group--age">
              <legend>
                <span aria-hidden="true">👤</span> 
                {' '}
                {t('introduction-age-and-residence')}
              </legend>
              <div className="profile-form__inline-fields">
                <label className={`profile-form__field${isKeyboardOpen && activeInput === 'age' ? ' profile-form__field--active' : ''}`} htmlFor="age">
                  {t('introduction-age')}
                  <input id="age" name="age" type="number" data-keyboard-target min="1" max="120" inputMode="numeric" value={profile.age} onFocus={() => { setActiveInput('age'); setIsResidenceSearchOpen(false); if (enableKeyboard) setIsKeyboardOpen(true); }} onKeyDown={handleAgeKeyDown} onChange={handleProfileChange} required />
                </label>
                <label className={`profile-form__field profile-form__field--search${isKeyboardOpen && activeInput === 'residence' ? ' profile-form__field--active' : ''}`} htmlFor="residence">
                  {t('introduction-residence')}
                  <div className="profile-form__search-control">
                    <input id="residence" name="residence" type="search" data-keyboard-target autoComplete="off" role="combobox" aria-autocomplete="list" aria-controls="residence-suggestions" aria-expanded={isResidenceSearchOpen} value={profile.residence} onFocus={() => { setActiveInput('residence'); setIsResidenceSearchOpen(true); if (enableKeyboard) setIsKeyboardOpen(true); }} onChange={(event) => { handleProfileChange(event); setIsResidenceSearchOpen(true); }} required />
                    {profile.residence && <button type="button" className="profile-form__clear-residence" aria-label={t('introduction-clear-residence')} onPointerDown={event => event.preventDefault()} onClick={() => { updateProfileField('residence', ''); setIsResidenceSearchOpen(false); }}><img src="/img/icons/times.svg" alt="" aria-hidden="true" /></button>}
                    {isResidenceSearchOpen && residenceOptions.length > 0 && (
                    <ul id="residence-suggestions" className="profile-form__suggestions" role="listbox">
                      {residenceOptions.map(option => (
                        <li key={getResidenceOptionKey(option)} role="option">
                          <button type="button" onClick={() => { updateProfileField('residence', option.label); setIsResidenceSearchOpen(false); }}>
                            <span>{option.label}</span>
                            <small>{option.postcodes ? getPostcodeLabel(option) : t('introduction-country')}</small>
                          </button>
                        </li>
                      ))}
                    </ul>
                    )}
                  </div>
                </label>
              </div>
            </fieldset>
            <fieldset className="profile-form__group">
              <legend>
                <span aria-hidden="true">🚆</span> 
                {' '}
                {t('introduction-favorite-transport')}
              </legend>
              <div className="profile-form__options">
                {transportOptions.map(option => (
                  <label className="profile-form__option" key={option.value}>
                    <input type="radio" name="transport" value={option.value} checked={profile.transport === option.value} onChange={handleProfileChange} required />
                    <span>{t(option.translationKey)}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <fieldset className="profile-form__group">
              <legend>
                <span aria-hidden="true">🎓</span> 
                {' '}
                {t('introduction-highest-education')}
              </legend>
              <div className="profile-form__options">
                {educationOptions.map(option => (
                  <label className="profile-form__option" key={option.value}>
                    <input type="radio" name="education" value={option.value} checked={profile.education === option.value} onChange={handleProfileChange} required />
                    <span>{t(option.translationKey)}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <fieldset className="profile-form__group">
              <legend>
                <span aria-hidden="true">🧠</span> 
                {' '}
                {t('introduction-user-type')}
              </legend>
              <div className="profile-form__options profile-form__options--types">
                {userTypes.map(option => (
                  <label className="profile-form__option" key={option.value}>
                    <input type="radio" name="userType" value={option.value} checked={profile.userType === option.value} onChange={handleProfileChange} required />
                    <span>{t(option.translationKey)}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="profile-form__actions">
              <Button type="submit">{t('introduction-start-button')}</Button>
              <Button type="button" className="button profile-form__back-button" onClick={() => { setShowProfileForm(false); navigate('.', { replace: true, state: { isProfileFormOpen: false } }); setIsKeyboardOpen(false); setIsResidenceSearchOpen(false); setKeyboardHeight(0); }}>{t('introduction-back-button')}</Button>
            </div>
          </form>
        )}
        {enableKeyboard && showProfileForm && <Keyboard isOpen={isKeyboardOpen} onClose={() => { setIsKeyboardOpen(false); setIsResidenceSearchOpen(false); setKeyboardHeight(0); }} onHeightChange={setKeyboardHeight} value={profile[activeInput]} layoutName={activeInput === 'age' ? 'numbers' : 'default'} onChange={(value) => { updateProfileField(activeInput, value); }} />}
      </div>
    </section>
  );
}

export default Introduction;
